'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useState, type ReactNode } from 'react';

import { accountBunja } from '@flipster/r230522/original/account';
import type { OpenPosition } from '@flipster/r230522/original/position';
import type { DesktopTradeCallbacks, TpSlDragIntent } from '@flipster/tvchart';

import { useClosePositionModal } from '../position-close-modal/use-close-position-modal';
import { SymbolScope } from '../shared/symbol/bunja';
import { tradingSessionCheckBunja } from '../shared/trading-suspension/state';
import { ChartTpSlDragOpener } from './chart-tpsl-drag-opener';
import { useOrderDragMutation } from './use-order-drag-mutation';

/**
 * 데스크톱 차트 거래 콜백 묶음 (webview의 `useWebviewTradeCallbacks`와 대칭).
 *
 * 콜백은 v2/trade API로 **직접 실행**한다(use-order-drag-mutation / partial-close modal).
 * Flow A 생성은 web TP/SL 모달(`ChartTpSlDragOpener`)을 연다 — 이게 로컬 state(`pendingTpSlDrag`)
 * + 모달 렌더와 묶여 있어, 콜백과 함께 렌더해야 하는 opener 노드(`dragOpener`)도 같이 돌려준다.
 */
export function useDesktopTradeCallbacks({ symbol }: { symbol: string }): {
  tradeCallbacks: DesktopTradeCallbacks;
  dragOpener: ReactNode;
} {
  const t = useTranslations();
  const { checkTradable } = useBunja(tradingSessionCheckBunja, [SymbolScope.bind(symbol)]);
  const { onDragComplete, onCancelClick } = useOrderDragMutation({ symbol });
  const { onOpenPartialCloseModal } = useClosePositionModal();
  const { v2AccountAtom } = useBunja(accountBunja);
  const account = useAtomValue(v2AccountAtom);

  // 드래그(생성/수정)로 도출된 intent를 보관 → ChartTpSlDragOpener가 TP/SL 모달을 prefill해 연다(orderId로 분기).
  const [pendingTpSlDrag, setPendingTpSlDrag] = useState<TpSlDragIntent | null>(null);
  const onOpenTpSlModal = useCallback((intent: TpSlDragIntent) => setPendingTpSlDrag(intent), []);

  // Flow C: 포지션 라인 ×는 full close가 아니라 partial close 모달을 연다 (타이틀은 "Close").
  const onPositionClose = useCallback(
    (position: OpenPosition) => {
      if (!checkTradable()) return;
      onOpenPartialCloseModal(position, account?.tradeMode, { title: t('Common.r250401.Close') });
    },
    [onOpenPartialCloseModal, account?.tradeMode, checkTradable, t],
  );

  const tradeCallbacks: DesktopTradeCallbacks = useMemo(
    () => ({
      onOrderDragComplete: onDragComplete,
      onOrderCancelClick: onCancelClick,
      onPositionClose,
      onOpenTpSlModal,
    }),
    [onDragComplete, onCancelClick, onPositionClose, onOpenTpSlModal],
  );

  const dragOpener = pendingTpSlDrag ? (
    <ChartTpSlDragOpener
      key={`${pendingTpSlDrag.positionId}-${pendingTpSlDrag.kind}-${pendingTpSlDrag.orderId ?? 'new'}-${pendingTpSlDrag.triggerPrice}`}
      symbol={symbol}
      intent={pendingTpSlDrag}
      onDone={() => setPendingTpSlDrag(null)}
    />
  ) : null;

  return { tradeCallbacks, dragOpener };
}
